/**
 * Live countdown to the next cap reset, used by the Drills CappedBanner
 * and the cap-reached UpgradeModal so both surfaces show the same
 * "unlock in X" wording.
 *
 * Daily caps reset at the next UTC midnight. Monthly caps reset at the
 * start of the next UTC month. Ticks once per minute — sub-minute
 * precision adds visual jitter for no benefit.
 *
 * Return shape: a string prefixed with "in " so callers can drop it
 * inline ("Drills unlock {text}", "Resets {text}"). When window is
 * "none" the text is empty.
 */

import { useEffect, useState } from "react";

export type GatingWindowKind = "daily" | "monthly" | "none";

export function useResetCountdown(window: GatingWindowKind): string {
  const [text, setText] = useState(() => computeCountdownText(window));
  useEffect(() => {
    if (window === "none") {
      setText("");
      return;
    }
    setText(computeCountdownText(window));
    const id = setInterval(() => setText(computeCountdownText(window)), 60_000);
    return () => clearInterval(id);
  }, [window]);
  return text;
}

export function computeCountdownText(window: GatingWindowKind): string {
  if (window === "none") return "";
  const now = new Date();
  const target =
    window === "daily" ? nextUtcMidnight(now) : nextUtcMonthStart(now);
  const ms = Math.max(0, target.getTime() - now.getTime());
  return formatDuration(ms, window);
}

function nextUtcMidnight(from: Date): Date {
  return new Date(
    Date.UTC(
      from.getUTCFullYear(),
      from.getUTCMonth(),
      from.getUTCDate() + 1,
      0,
      0,
      0,
      0,
    ),
  );
}

function nextUtcMonthStart(from: Date): Date {
  return new Date(
    Date.UTC(from.getUTCFullYear(), from.getUTCMonth() + 1, 1, 0, 0, 0, 0),
  );
}

function formatDuration(ms: number, window: "daily" | "monthly"): string {
  const totalMinutes = Math.max(1, Math.floor(ms / 60_000));
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;

  if (window === "daily") {
    if (hours > 0) return `in ${hours}h ${minutes}m`;
    return `in ${minutes}m`;
  }
  if (days > 0) return `in ${days}d ${hours}h`;
  if (hours > 0) return `in ${hours}h ${minutes}m`;
  return `in ${minutes}m`;
}
